<template>
  <div class="table-container">
    <table>
      <thead>
        <tr>
          <th class="item-cell">
            <div>Item</div>
          </th>

          <th
            v-for="field in fields"
            :key="field.value"
            class="sort-cell"
            @click="sortItems(field.value)"
          >
            <div>
              <icon
                v-show="field.value === sort"
                :name="descending ? 'arrow_downward' : 'arrow_upward'"
              />

              <span>{{ field.label }}</span>
            </div>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="item in itemList"
          :key="item.id"
          :style="{ '--graph-color': item.color }"
        >
          <td class="item-cell">
            <router-link v-if="type === 'cards'" class="item" :to="{ path: '/compare', query: { ...$route.query, cardId: item.id } }">
              <div class="card-item-details">
                <card-image :image-url="item.image" />

                <card-item-info :card="item">
                  <icon-button
                    icon="delete_outline"
                    @click.stop.prevent="$emit('removeItem', item.id)"
                  />
                </card-item-info>
              </div>
            </router-link>

            <div v-if="type === 'indexes'" class="item">
              <div class="index-item-details">
                <index-image :index="item" />

                <div class="label">
                  {{ item.label }}
                </div>

                <icon-button
                  icon="delete_outline"
                  @click.stop.prevent="$emit('removeItem', item.id)"
                />
              </div>
            </div>
          </td>

          <td
            v-for="field in fields"
            :key="field.value"
            :class="{ success: item[`${field.value}Success`], error: item[`${field.value}Error`] }"
          >
            {{ item[`${field.value}Display`] }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { getCompareCardsData, getCompareIndexesData } from '@cardladder/shared/utils/compare.util';

import CardItemInfo from '@cardladder/shared/components/CardItemInfo';
import CardImage from '@cardladder/shared/components/CardImage';
import IndexImage from '@cardladder/shared/components/IndexImage';

export default {
  components: {
    CardItemInfo,
    CardImage,
    IndexImage,
  },

  props: {
    zoom: [Number, Object],
    type: String,
    items: {
      type: Array,
      default() {
        return [];
      },
    },
  },

  data() {
    return {
      sort: 'originalValue',
      descending: true,
    };
  },

  computed: {
    itemList() {
      let list = [];

      if (this.type === 'cards') {
        list = getCompareCardsData(this.items, this.zoom);
      } else if (this.type === 'indexes') {
        list = getCompareIndexesData(this.items, this.zoom);
      }

      list.sort((a, b) => {
        let value = 0;
        const aValue = a[this.sort];
        const bValue = b[this.sort];

        if (!aValue) value = -1;
        if (!bValue) value = 1;
        if (aValue === bValue) value = 0;
        if (typeof aValue === 'number' && typeof bValue === 'number' && aValue !== bValue) value = aValue - bValue;

        return this.descending ? value * -1 : value;
      });

      return list;
    },

    fields() {
      if (this.type === 'cards') {
        return [
          { value: 'marketValue', label: 'Last Sold' },
          { value: 'originalValue', label: 'Start Value' },
          { value: 'pop', label: 'Pop' },
          { value: 'currentValue', label: 'Value' },
          { value: 'marketCap', label: 'Market Cap' },
          { value: 'numSales', label: '# Sales' },
          { value: 'dollarChange', label: '$ Change' },
          { value: 'percentChange', label: '% Change' },
        ];
      }

      return [
        { value: 'originalValue', label: 'Start Value' },
        { value: 'dailyIndex', label: 'Value' },
        { value: 'dailySalesCount', label: '24H Sales Count' },
        { value: 'dailySales', label: '24H Sales Volume' },
        { value: 'totalCards', label: '# Cards' },
        { value: 'realChange', label: 'Index Change' },
        { value: 'percentChange', label: '% Change' },
      ];
    },
  },

  methods: {
    sortItems(value) {
      if (this.sort === value) {
        this.descending = !this.descending;
      } else {
        this.sort = value;
        this.descending = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.table-container {
  box-shadow: $elevation-1;
  border-radius: $border-radius;
  background-color: var(--theme-card);
  margin-top: $gp;
}

.card-image {
  --card-image-width: 58px;
  margin-right: $gp;
}

table {
  position: relative;
  background-color: transparent;
}

td,
th {
  background: transparent;
  white-space: nowrap;
}

thead tr,
tbody tr {
  border-bottom: 1px solid var(--theme-input-border);
}

.item-cell {
  padding: 0;
  background-color: var(--theme-card);

  @media($medium-up) {
    position: -webkit-sticky;
    position: sticky;
    left: 0;
  }
}

.sort-cell {
  &:hover {
    background-color: var(--theme-highlight);
    cursor: pointer;
  }

  > div {
    display: flex;
    align-items: center;
    justify-content: flex-end;

    > span {
      margin-left: 3px;
    }
  }
}

td:not(.item-cell) {
  font-size: 16px;
  font-family: $font-family-secondary;
}

th:not(.item-cell),
td:not(.item-cell) {
  text-align: right;
  padding: $gp $gp * .5;
}

thead th:first-child {
  left: 0;
  z-index: 1;
  padding: 0;

  > div {
    padding: $gp;
    border-right: 1px solid var(--theme-input-border);
  }
}

th:last-child,
td:last-child {
  padding-right: $gp;
}

.item {
  display: block;
  border-left: 6px solid var(--graph-color);
  border-right: 1px solid var(--theme-input-border);
  text-decoration: none !important;
  color: var(--theme-color);

  &:hover {
    background-color: var(--theme-highlight);
    cursor: pointer;
  }

  &.router-link-exact-active {
    background-color: var(--theme-active);
  }
}

.card-item-details {
  display: flex;
  padding: $gp;

  img {
    object-fit: cover;
    border-radius: $border-radius;
    margin-right: $gp;
    min-width: 60px;
    max-width: 60px;
  }
}

.index-item-details {
  display: flex;
  align-items: center;
  padding: $gp;

  .label {
    @include ellipsis;
    font-size: 22px;
    font-weight: bold;
    flex: 1;
    margin: 0 $gp * .5;
  }
}
</style>
